<template>
  <div v-if="isLoading">
    <SpinnerView />
  </div>
  <div v-else-if="fetchError" class="space-y-4">
    <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400" aria-label="breadcrumb">
      <router-link :to="{ name: 'Zones' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
        Zones
      </router-link>
      <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
      <span class="text-neutral-900 dark:text-neutral-100 font-medium">Edit Zone</span>
    </nav>
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700">
      <ErrorState title="Couldn't load this zone" :message="fetchError" @retry="getZoneData" />
    </div>
  </div>
  <div v-else-if="notFound" class="space-y-4">
    <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400" aria-label="breadcrumb">
      <router-link :to="{ name: 'Zones' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
        Zones
      </router-link>
      <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
      <span class="text-neutral-900 dark:text-neutral-100 font-medium">Edit Zone</span>
    </nav>
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 pb-8">
      <ErrorState
        title="Zone not found"
        message="This zone doesn't exist for the current site. It may have been deleted, or it belongs to another site."
        :show-retry="false"
      />
      <div class="text-center">
        <router-link
          :to="{ name: 'Zones' }"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-colors"
        >
          Back to Zones
        </router-link>
      </div>
    </div>
  </div>
  <div v-else class="zone-view space-y-6">
    <!-- Header -->
    <div>
      <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400 mb-1" aria-label="breadcrumb">
        <router-link
          :to="{ name: 'Zones' }"
          class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          Zones
        </router-link>
        <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
        <span class="text-neutral-900 dark:text-neutral-100 font-medium">{{ zoneName || 'Edit Zone' }}</span>
      </nav>
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Edit Zone</h1>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Form -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Zone Details</h2>
        </div>
        <div class="p-6">
          <form @submit.prevent="saveZone" class="space-y-5">
            <div>
              <label for="zone-name" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Zone Name</label>
              <input
                id="zone-name"
                class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                type="text"
                required
                v-model="zoneName"
              />
            </div>
            <div>
              <label for="zone-description" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Description</label>
              <input
                id="zone-description"
                class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                type="text"
                v-model="zoneDescription"
              />
            </div>

            <!-- Geo Bounds -->
            <div>
              <span class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
                Geo Bounds ({{ zoneData.areas?.length || 0 }} points)
              </span>
              <div class="space-y-2">
                <div
                  v-for="(area, index) in zoneData.areas"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs text-neutral-400 w-5 text-center flex-shrink-0">{{ index + 1 }}</span>
                  <input
                    class="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm tabular-nums"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    placeholder="Latitude"
                    :aria-label="`Point ${index + 1} latitude`"
                    v-model="area.lat"
                  />
                  <input
                    class="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm tabular-nums"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    placeholder="Longitude"
                    :aria-label="`Point ${index + 1} longitude`"
                    v-model="area.lng"
                  />
                  <button
                    type="button"
                    @click="removeArea(index)"
                    :aria-label="`Remove point ${index + 1}`"
                    class="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <span class="material-icons text-[16px]" aria-hidden="true">close</span>
                  </button>
                </div>
              </div>
              <button
                type="button"
                @click="addArea"
                class="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
              >
                <span class="material-icons text-[16px]" aria-hidden="true">add</span>
                Add point
              </button>
            </div>

            <div v-if="saveError" role="alert" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
              <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
              <p class="text-xs text-red-700 dark:text-red-400">{{ saveError }}</p>
            </div>

            <div class="pt-2">
              <button
                type="submit"
                :disabled="isSaving"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="isSaving" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSaving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Map Preview -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Map Preview</h2>
        </div>
        <GMapMap
          :zoom="12"
          :center="coords"
          map-type-id="terrain"
          style="width: 100%; height: 500px"
          :options="{
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          }"
        >
          <GMapPolygon
            v-if="zoneData.areas?.length"
            :paths="zoneData.areas"
            :options="{
              fillColor: '#1E90FF',
              fillOpacity: 0.2,
              strokeColor: '#1E90FF',
              strokeWeight: 2,
            }"
          />
        </GMapMap>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import useToast from "@/composables/useToast";
import SpinnerView from "@/components/SpinnerView.vue";
import ErrorState from "@/components/ui/ErrorState.vue";

export default {
  components: { SpinnerView, ErrorState },
  props: ["id"],
  setup(props) {
    const store = useStore();
    const { showToast } = useToast();
    const zoneName = ref("");
    const zoneDescription = ref("");
    const coords = ref({ lat: 25.15, lng: 55.25 });
    const isLoading = ref(true);
    const fetchError = ref(null);
    const notFound = ref(false);
    const zoneData = ref({ areas: [] });
    const isSaving = ref(false);
    const saveError = ref(null);

    const getZoneData = async () => {
      isLoading.value = true;
      fetchError.value = null;
      notFound.value = false;
      try {
        const apiUrl = `${store.state.apiUrl}/getZonePlannerData/${store.state.currentSite}`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const result = await response.json();
        const zone = result.find((z) => z.zoneId === props.id);
        if (zone) {
          zoneName.value = zone.zoneName;
          zoneDescription.value = zone.zoneDescription;
          zoneData.value = zone;
          coords.value = calculateCenter(zone.areas);
        } else {
          notFound.value = true;
        }
      } catch (error) {
        console.error("Failed to fetch zone:", error);
        fetchError.value = error.message || "Request failed.";
      } finally {
        isLoading.value = false;
      }
    };

    const calculateCenter = (areas) => {
      if (!areas?.length) return { lat: 25.15, lng: 55.25 };
      const total = areas.reduce((acc, a) => ({ lat: acc.lat + a.lat, lng: acc.lng + a.lng }), { lat: 0, lng: 0 });
      return { lat: total.lat / areas.length, lng: total.lng / areas.length };
    };

    const addArea = () => {
      // Seed from the polygon center, not 0,0 — a 0,0 point instantly drags
      // the preview polygon to the Gulf of Guinea.
      zoneData.value.areas = [...zoneData.value.areas, { ...coords.value }];
    };

    const removeArea = (index) => {
      zoneData.value.areas = [
        ...zoneData.value.areas.slice(0, index),
        ...zoneData.value.areas.slice(index + 1),
      ];
    };

    const saveZone = async () => {
      if (isSaving.value) return;
      saveError.value = null;

      const hasInvalidPoint = zoneData.value.areas.some(
        (a) => a.lat === "" || a.lng === "" || a.lat === null || a.lng === null ||
          Number.isNaN(Number(a.lat)) || Number.isNaN(Number(a.lng))
      );
      if (hasInvalidPoint) {
        saveError.value = "Every point needs both a latitude and a longitude.";
        return;
      }

      isSaving.value = true;
      try {
        // Zones live in the "zones" Firestore collection keyed by zoneId,
        // with the same shape Zones.vue's create flow posts to the API.
        await updateDoc(doc(db, "zones", props.id), {
          zoneName: zoneName.value,
          zoneDescription: zoneDescription.value || "",
          areas: zoneData.value.areas.map((a) => ({ lat: Number(a.lat), lng: Number(a.lng) })),
        });
        coords.value = calculateCenter(zoneData.value.areas);
        showToast("success", "Zone updated");
      } catch (error) {
        console.error("Failed to save zone:", error);
        saveError.value = "Could not save this zone. Please try again.";
        showToast("error", "Failed to update zone. Please try again.");
      } finally {
        isSaving.value = false;
      }
    };

    getZoneData();

    return {
      zoneName,
      zoneDescription,
      coords,
      isLoading,
      fetchError,
      notFound,
      zoneData,
      isSaving,
      saveError,
      addArea,
      removeArea,
      saveZone,
      getZoneData,
    };
  },
};
</script>

<style scoped>
.zone-view > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.zone-view > *:nth-child(1) { animation-delay: 0s; }
.zone-view > *:nth-child(2) { animation-delay: 0.06s; }

@media (prefers-reduced-motion: reduce) {
  .zone-view > * {
    animation: none;
  }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
